/* eslint-disable no-console*/

import * as Sentry from '@sentry/react';
import axios from 'axios';
import get from 'lodash.get';

import noop from 'ui-components/default-props/noop';
import GuidService from 'ui-components/services/guid-service';

import { buffer$, logs$ } from './buffer';

/**
 * @callback LogCallback
 * @param {string} message
 * @param {object} extra
 */

const consoleError = console.error.bind(console);
const consoleWarning = console.warn.bind(console);
const consoleInfo = console.info.bind(console);

const environment = typeof window !== 'undefined' ? get(window, 'SS_GLOBALS.environment', 'dev') : 'dev';
const isInDevelopmentMode = environment === 'dev';
const shouldLogToBrowser = isInDevelopmentMode;

/**
 * @param {import('@sentry/react').Scope} scope
 * @param {object} extra
 */
const setExtraContext = (scope, extra) => {
  if (extra !== undefined) {
    scope.setExtra('additionalContext', extra);
  }
};

class LogService {
  /**
   * @param {object} metadata
   */
  constructor(metadata) {
    this.metadata = metadata;
  }

  /**
   * @param {object} metadata
   */
  extendMetadata = (metadata) => {
    try {
      this.metadata = { ...this.metadata, ...metadata };
    } catch (e) {
      this.logError(String(e), {
        metadataExtension: metadata,
        existingMetadata: this.metadata,
      });
    }
  };

  /**
   * @type {LogCallback}
   */
  logInfo = shouldLogToBrowser
    ? consoleInfo
    : (msg, context) => {
        buffer$.next({ metadata: this.metadata, msg, context });
      };

  /**
   * @type {LogCallback}
   */
  logWarning = shouldLogToBrowser
    ? consoleWarning
    : (message, extra) => {
        Sentry.withScope((scope) => {
          setExtraContext(scope, extra);
          scope.setLevel('warning');
          Sentry.captureMessage(message, scope);
        });
      };

  /**
   * @type {LogCallback}
   */
  logError = shouldLogToBrowser
    ? consoleError
    : (message, extra) => {
        Sentry.withScope((scope) => {
          setExtraContext(scope, extra);
          Sentry.captureException(message, scope);
        });
      };
}

const logService = new LogService({ sessionGuid: GuidService.newGuid() });

const MAX_LOGS_PER_REQUEST = 5;

/**
 * @template T
 * @param {T[]} array
 * @param {number} size
 * @returns {T[][]}
 */
const chunkArray = (array, size) => {
  if (size <= 0) return [array];
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

logs$.subscribe((arrayOfLogs) => {
  if (arrayOfLogs.length >= 500) {
    logService.logError('log buffer overflow', {
      bufferLength: arrayOfLogs.length,
      logSample: arrayOfLogs[arrayOfLogs.length - 1],
    });
  } else if (arrayOfLogs.length) {
    const logChunks = chunkArray(arrayOfLogs, MAX_LOGS_PER_REQUEST);
    logChunks.forEach((chunk) => {
      axios.post('', chunk, { baseURL: '/log-bulk' }).catch(noop);
    });
  }
});

export default logService;
